import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown } from "lucide-react";
import { usePhoneNumberInput } from "@frontend-common/hooks/use-phone-number-input";
import { countryFlag, localizedCountryName } from "@common/logic/countries";
import {
  nationalNumberExample,
  searchPhoneCountries,
} from "@common/logic/phone-countries";
import { CountryListOverlay } from "@/components/country-list-overlay";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** Country calling-code picker + national number; `value` is the full "+36701234567". */
export function PhoneNumberInput({
  id,
  tabIndex,
  value,
  onChange,
  onBlur,
  onEnterSubmit,
  disabled,
  hasError,
}: {
  id?: string;
  tabIndex: number;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onEnterSubmit?: () => void;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const { i18n } = useTranslation();
  const phone = usePhoneNumberInput({ value, onChange });
  const [open, setOpen] = useState(false);

  return (
    <div className="flex w-full items-center gap-2">
      <button
        type="button"
        disabled={disabled}
        aria-label={localizedCountryName(phone.country, i18n.language)}
        onClick={() => setOpen(true)}
        className={cn(
          "flex h-14 shrink-0 items-center gap-2 rounded-md border-2 border-bub-dark-blue bg-white px-3 text-[24px] transition-colors",
          disabled && "bg-gray-200 text-gray-500",
          hasError && "border-red-500",
        )}
      >
        <span className="text-[28px] leading-none">
          {countryFlag(phone.country.code)}
        </span>
        <span>+{phone.country.dialCode}</span>
        <ChevronsUpDown className="h-5 w-5 shrink-0 opacity-60" />
      </button>
      <Input
        id={id}
        tabIndex={tabIndex}
        type="tel"
        value={phone.national}
        onChange={phone.setNational}
        onBlur={onBlur}
        onEnterSubmit={onEnterSubmit}
        disabled={disabled}
        placeholder={nationalNumberExample(phone.country.code)}
        className={cn(hasError && "border-red-500")}
      />

      {open && (
        <CountryListOverlay
          search={(query) => searchPhoneCountries(query, i18n.language)}
          selectedCode={phone.country.code}
          onSelect={(country) => {
            phone.setCountry(country);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
          renderSuffix={(country) => (
            <span className="text-gray-500">+{country.dialCode}</span>
          )}
        />
      )}
    </div>
  );
}
